import { Breadcrumb, Table, Cell, Tabs, Pagination, BookingAlertBadge } from 'components'
import { LoadingFullScreen } from 'components/Loading/FullScreen'
import { DateTime } from 'luxon'
import { getBookings, getStatusTotals } from 'api/bookings'
import { getAllDepartments } from 'api/clients'
import { debounce } from 'utils/debounce'
import { isClientAdmin, isDelegate } from 'store/profile'

const statusCards = [
  { title: 'New Requests', statusKey: 'NewRequest', tabName: 'New Requests', showExpired: true },
  { title: 'Awaiting Delegate', statusKey: 'AwaitingDelegate', tabName: 'Awaiting Delegate', showExpired: true },
  { title: 'Awaiting Reservation No', statusKey: 'AwaitingReservation', tabName: 'Awaiting Res No', showExpired: true },
  { title: 'Awaiting PO', statusKey: 'AwaitingPONumber', tabName: 'Awaiting PO No', showExpired: false },
  { title: 'Amendment', statusKey: 'Amendment', tabName: 'Amendment', showExpired: false }
]

const StatusCardRow = () => {
  return {
    view ({ attrs: { label, value, highlightClass } }) {
      return m('dd.grid.grid-cols-2', { class: value > 0 && highlightClass ? highlightClass : 'text-gray-900' }, [
        m('div.text-lg.font-medium', label),
        m('div.text-lg.font-medium', value)
      ])
    }
  }
}

const StatusCard = () => {
  return {
    view ({ attrs: { title, totals, showExpired, onViewAll } }) {
      const valueOf = (field) => {
        if (totals) {
          return totals[field]
        }
        return 0
      }
      return m('div.bg-white.overflow-hidden.shadow.rounded-lg.flex.flex-col.justify-between', [
        m('div.p-5',
          m('dl', [
            m('dt.text-sm.font-medium.text-gray-500.truncate', title),
            m(StatusCardRow, { label: 'Total', value: valueOf('total') }),
            showExpired && m(StatusCardRow, { label: 'Expired', value: valueOf('expired'), highlightClass: 'text-red-300' }),
            m(StatusCardRow, { label: 'Urgent', value: valueOf('urgent'), highlightClass: 'text-orange-600' }),
            m(StatusCardRow, { label: 'Alerts', value: valueOf('alerts'), highlightClass: 'text-red-600' })
          ])
        ),
        m('div.bg-gray-50.px-5.py-3',
          m('div.text-sm',
            m("a.font-medium.text-cyan-700.hover:text-cyan-900[href='#']", {
              onclick: (e) => {
                e.preventDefault()
                onViewAll()
              }
            }, ' View all ')
          )
        )
      ])
    }
  }
}

export const AccommodationDashboard = ({ attrs: { tab } }) => {
  if (isClientAdmin()) {
    m.route.set('/client/dashboard')
  } else if (isDelegate()) {
    m.route.set('/booking')
  }

  const filters = { page: 1, search: null, statusId: 0, sortby: 'arrival' }
  let bookings
  let meta
  let departments
  let statusTotals
  let activeTab

  getAllDepartments({ pagesize: 100 }).then(data => {
    if (data) { departments = data.data }
  })

  getStatusTotals().then(data => {
    if (data) {
      statusTotals = data
    }
  })

  const filterDepartments = (val) => {
    filters.departmentId = val
    filters.page = 1
    getBookings(filters).then(data => {
      if (data) {
        bookings = data.data
        meta = data.meta
      }
    })
  }

  const debouncedSearch = debounce(val => {
    filters.search = val.length > 2 ? val : null
    refreshData()
  }, 300)

  const refreshData = () => {
    getBookings(filters).then(data => {
      if (data) {
        bookings = data.data
        meta = data.meta
      }
    })

    var pageUrl = window.location.pathname + `?tab=${activeTab ? activeTab.name : ''}`
    window.history.pushState('', '', pageUrl)
  }

  const tabs = [{ name: 'New Requests', statusId: 0 }, { name: 'Awaiting Delegate', statusId: 1 }, { name: 'Awaiting Res No', statusId: 2 }, { name: 'Awaiting PO No', statusId: 7 }, { name: 'Amendment', statusId: 10 }].map((t, ix) => {
    return {
      id: ix + 1,
      name: t.name,
      statusId: t.statusId
    }
  })

  const selectTab = (name) => {
    tabs.forEach(t => {
      t.active = false
      if (t.name === name) {
        t.active = true
        activeTab = t
      }
    })
    filters.statusId = activeTab.statusId
    refreshData()
  }

  selectTab(tab || 'New Requests')

  return {
    view () {
      return [
        m(Breadcrumb, {
          links: [{
            title: `${t('app', 'accommodation')} ${t('app', 'dashboard')}`
          }]
        }),
        m('.mt-4.px-4.sm:px-6.lg:px-8',
          [
            m('.mt-2.grid.grid-cols-1.gap-5.sm:grid-cols-2.lg:grid-cols-3.xl:grid-cols-5',
              statusCards.map(card => m(StatusCard, {
                key: card.tabName,
                ...card,
                totals: statusTotals && statusTotals[card.statusKey],
                onViewAll: () => selectTab(card.tabName)
              }))
            ),

            m('.mt-3', [
              m(Tabs, {
                tabs: tabs,
                onTabChange: (tabId) => {
                  tabs.forEach(t => {
                    if (t.id === tabId) {
                      selectTab(t.name)
                    }
                  })
                }
              }),
              m('', { style: { maxHeight: 'calc(100vh - 18rem)' } }, [
                m('div.shadow.h-full.border-b.border-gray-200.sm:rounded-lg',
                  bookings && departments ? m(Table, {
                    cols: [
                      {
                        label: 'Name',
                        search: true,
                        searchPlaceholder: 'Search name or email...',
                        filterClasses: 'flex-2',
                        searchOnChange: (val) => {
                          debouncedSearch(val)
                        }
                      },
                      {
                        label: 'Email',
                        // classes: ['w-96'],
                        filterClasses: 'hidden'
                      },
                      {
                        label: 'Department',
                        classes: ['w-48'],
                        filter: true,
                        filterOptions: departments,
                        filterEmptyOption: true,
                        filterOnChange: (val) => {
                          filterDepartments(val)
                        }
                      },
                      {
                        label: 'Venue',
                        classes: ['w-48']
                        // filter: true,
                        // filterOptions: venues,
                        // filterEmptyOption: true,
                        // filterOnChange: (val) => {
                        //   filterDepartments(val)
                        // }
                      },
                      {
                        label: 'Arrival',
                        classes: ['w-32 justify-center']
                      },
                      {
                        label: 'Nights',
                        classes: ['w-12 justify-center']
                      },
                      {
                        label: 'Actioned',
                        classes: ['w-40 justify-center']
                      },
                      {
                        label: '',
                        search: false,
                        classes: ['w-12 justify-center']
                      }
                    ],
                    filters: true
                  },
                  [
                    m('.overflow-auto.flex-grow',
                      bookings.length ? bookings.map((v, ix) => {
                        return m(BookingListItem, { item: v, key: v.id })
                      }) : m('p.text-center.p-3.text-gray-400', 'No results')
                    ),
                    meta && m(Pagination, {
                      meta: meta,
                      onpage: (page) => {
                        filters.page = page
                        getBookings(filters).then(data => {
                          bookings = data.data
                          meta = data.meta
                        })
                      }
                    })
                  ]) : m(LoadingFullScreen)
                )
              ])
            ])
          ]
        )
      ]
    }
  }
}

const BookingListItem = ({ attrs: { item, classes = [], clientId, departmentId } }) => {
  const newFormat = { ...DateTime.DATETIME_SHORT, year: '2-digit' }
  return {
    view () {
      const dateDiff = (DateTime.fromISO(item.arrivalDate)).diff(DateTime.now().startOf('day'), 'days')
      const rowColor = dateDiff.as('days') < 0 ? 'bg-red-200' : dateDiff.as('days') <= 5 ? 'bg-orange-200' : 'bg-white'
      return m(m.route.Link, {
        class: ['flex', 'flex-shrink-0', 'hover:bg-gray-300', 'cursor-pointer', rowColor, ...classes].join(' '),
        'data-days': dateDiff.as('days'),
        style: {
          height: '40px'
        },
        href: `/clients/${item.clientId}/departments/${item.departmentId}/bookings/${item.id}`
      }, [
        m(Cell,
          item.name
        ),
        m(Cell, { cellClasses: ['tracking-tight'] },
          item.email
        ),
        m(Cell, { width: 'w-48' },
          item.departmentName
        ),
        m(Cell, { width: 'w-48', cellClasses: ['tracking-tight'] },
          item.venueName || (item.venue1stChoice + ' *')
        ),
        m(Cell, { width: 'w-32', align: 'center' },
          DateTime.fromISO(item.arrivalDate).toLocaleString()
        ),
        m(Cell, { width: 'w-12', align: 'center', padding: 'py-3', noEllipsis: 'true' },
          item.nights
        ),
        m(Cell, { width: 'w-40', align: 'center', padding: 'px-2 py-3' },
          DateTime.fromISO(item.lastActionDate).toLocaleString(newFormat)
        ),
        m(Cell, { width: 'w-12', align: 'center', padding: 'px-3 py-2', noEllipsis: true },
          m(BookingAlertBadge, {
            alert: item.alertMessage, size: 'xs', margin: 'm-0'
          }
          )
        )
      ])
    }
  }
}
